import { Box, Button, Flex, FormControl, FormLabel, IconButton, Input, InputGroup, InputRightElement, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack, } from "@chakra-ui/react";
import { useState } from "react";
import { CustomToast } from "../../components";
import { useFetch } from "../../hooks/useFetch";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAuth } from "../../hooks/useAuth";

interface UpdatePasswordProps {
    isOpen: boolean;
    onClose: () => void;
}

export interface IUpdatePassword {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

const schema = yup.object().shape({
    currentPassword: yup
        .string()
        .required("Current password is required"),

    newPassword: yup
        .string()
        .required("New password is required")
        .min(8, "Password must be at least 8 characters")
        .max(16, "Password must be at most 16 characters")
        .matches(/^(?!\s+$).*$/, "Password cannot consist only of spaces")
        .matches(/^(?!\s).*(?<!\s)$/, "Password cannot start or end with spaces")
        .matches(/[A-Za-z]/, "Password must contain at least one alphabet (A-Z or a-z)")
        .matches(/\d/, "Password must contain at least one number (0-9)")
        .matches(/[!@#$%^&*(),.?":{}|<>_\-+=\\[\]/~`;']/,
            "Password must contain at least one special character")
        .notOneOf(
            [yup.ref("currentPassword")],
            "New password cannot be the same as the current password"
        ),

    confirmPassword: yup
        .string()
        .required("Confirm password is required")
        .oneOf([yup.ref("newPassword")], "Passwords must match"),
});

const UpdatePassword = ({ isOpen, onClose }: UpdatePasswordProps) => {
    const { addToast } = CustomToast();
    const { fetchApi, } = useFetch(addToast);
    const [loading, setLoading] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { logout } = useAuth();

    const { handleSubmit, control, reset, formState: { errors }, } = useForm<IUpdatePassword>({
        resolver: yupResolver(schema),
        defaultValues: { currentPassword: "", newPassword: "", confirmPassword: "", },
    });

    const onSubmit = async (data: IUpdatePassword) => {
        setLoading(true);
        const payload = {
            oldPassword: data.currentPassword.trim(),
            newPassword: data.newPassword.trim(),
        };
        const result = await fetchApi("user/updatepassword", "PUT", payload, null, "Password updated successfully");
        setLoading(false);
        if (result) {
            reset();
            logout();
            onClose();
        }
    };

    const preventClipboard = {
        onCopy: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
        onCut: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
        onPaste: (e: React.ClipboardEvent<HTMLInputElement>) => e.preventDefault(),
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <ModalContent>
                    <ModalHeader>Update Password</ModalHeader>

                    <ModalBody>
                        <Box >
                            <FormControl isInvalid={!!errors.currentPassword} >
                                <FormLabel>Current Password</FormLabel>
                                <Controller name="currentPassword" control={control} render={({ field }) => (
                                    <Input  {...field}  {...preventClipboard} type="password" placeholder="Current Password" autoComplete="current-password" />
                                )}
                                />
                                {errors.currentPassword && (<Text color="red.500" fontSize="sm">{errors.currentPassword.message}</Text>)}
                            </FormControl>
                        </Box>
                        <Box mt={4}>
                            <FormControl isInvalid={!!errors.newPassword} mt={4}>
                                <FormLabel>New Password</FormLabel>
                                <Controller name="newPassword" control={control} render={({ field }) => (
                                    <Input   {...field}   {...preventClipboard} type="password" placeholder="New Password" autoComplete="new-password" />
                                )}
                                />
                                {errors.newPassword && (
                                    <Text color="red.500" fontSize="sm">{errors.newPassword.message}</Text>
                                )}
                            </FormControl>
                        </Box>
                        <Box mt={4}>
                            <FormControl isInvalid={!!errors.confirmPassword} mt={4}>
                                <FormLabel>Confirm Password</FormLabel>

                                <Controller name="confirmPassword" control={control} render={({ field }) => (
                                    <InputGroup>
                                        <Input  {...field}  {...preventClipboard} type={showConfirmPassword ? "text" : "password"} placeholder="Confirm Password" autoComplete="new-password" />
                                        <InputRightElement>
                                            <IconButton aria-label="Toggle Password" variant="ghost" size="sm" icon={showConfirmPassword ? <ViewIcon /> : <ViewOffIcon />} onClick={() => setShowConfirmPassword(!showConfirmPassword)} />
                                        </InputRightElement>
                                    </InputGroup>
                                )}
                                />

                                {errors.confirmPassword && (<Text color="red.500" fontSize="sm">{errors.confirmPassword.message}</Text>)}
                            </FormControl>
                        </Box>
                    </ModalBody>

                    <ModalFooter gap={3}>
                        <Flex justifyContent="flex-end" mt={6}>
                            <Button variant="outline" mr={2} onClick={() => { reset(); onClose(); }}>Cancel</Button>

                            <Button colorScheme="green" isLoading={loading} type='submit'>Update                            </Button>
                        </Flex>

                    </ModalFooter>
                </ModalContent>
            </form>
        </Modal>
    );
};

export default UpdatePassword;